import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { IAgent } from "../types/agent";
import { IAgentStatus } from "../types/agent-status";
import { useAgents } from "../hooks/useAgents";
import { usePostHog } from "posthog-js/react";
import { useQueryClient } from "@tanstack/react-query";
import { useStatusService } from "../services/api/status-service";
import { useToast } from "./ToastContext";
import { useOrganization } from "@clerk/clerk-react";

const ACTIVE_AGENT_ID_KEY = "activeAgentId";

export const ActiveAgentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    data: agents,
    isLoading: agentsLoading,
    error: agentsError,
  } = useAgents();
  const { organization: activeOrg } = useOrganization();
  const [activeAgent, setActiveAgent] = useState<IAgent | null>(null);
  const [hasNoAgents, setHasNoAgents] = useState(false);
  const [isLoadingAgentStatus, setIsLoadingAgentStatus] = useState(false);
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const statusService = useStatusService();
  const { showSuccessToast, showErrorToast } = useToast();

  const getActiveAgentStorageKey = useCallback(() => {
    return activeOrg
      ? `${ACTIVE_AGENT_ID_KEY}_${activeOrg.id}`
      : ACTIVE_AGENT_ID_KEY;
  }, [activeOrg]);

  useEffect(() => {
    if (agentsError || !agents || !activeOrg) return;

    if (agents.length === 0) {
      setHasNoAgents(true);
      setActiveAgent(null);
      return;
    }

    setHasNoAgents(false);

    // Try to get stored agent ID for this organization
    const storageKey = getActiveAgentStorageKey();
    const storedAgentId = localStorage.getItem(storageKey);
    let storedAgent: IAgent | undefined;
    if (storedAgentId) {
      // Find the stored agent in the loaded agents
      storedAgent = agents.find((a) => a.id === storedAgentId);
      if (storedAgent) {
        setActiveAgent(storedAgent);
        return;
      }
    }

    // If no stored agent or stored agent not found, use first agent
    if (!activeAgent || !storedAgent) {
      setActiveAgent(agents[0]);
      localStorage.setItem(storageKey, agents[0].id);
    }
  }, [agents, activeOrg, getActiveAgentStorageKey]);

  const setActiveAgentById = useCallback(
    (id: string) => {
      const agent = agents?.find((a: IAgent) => a.id === id);
      if (agent && agent !== activeAgent) {
        trackAgentChange(agent, "manual_selection");
        setActiveAgent(agent);
        const storageKey = getActiveAgentStorageKey();
        localStorage.setItem(storageKey, id);
      }
    },
    [agents, activeAgent, getActiveAgentStorageKey]
  );

  const trackAgentChange = useCallback(
    (
      agent: IAgent,
      source: "manual_selection" | "url_param" | "initial_load"
    ) => {
      posthog.register({
        agent_id: agent.id,
        agent_name: agent.name,
      });

      // user manually selected the agent
      if (source === "manual_selection") {
        posthog.capture("agent_selected", {
          agent_id: agent.id,
          agent_name: agent.name,
        });
      }
    },
    [posthog]
  );

  const updateAgentStatusLocally = useCallback(
    (agentId: string, newStatus: Partial<IAgentStatus>) => {
      setActiveAgent((prev) => {
        if (!prev || prev.id !== agentId) return prev;
        return {
          ...prev,
          status: {
            ...prev.status,
            ...newStatus,
          },
        };
      });

      // update the agents list in the query cache, in the meantime while the query is refetching
      queryClient.setQueryData<IAgent[]>(["agents"], (oldAgents) => {
        if (!oldAgents) return oldAgents;
        return oldAgents.map((agent) => {
          if (agent.id !== agentId) return agent;
          return {
            ...agent,
            status: {
              ...agent.status,
              ...newStatus,
            },
          };
        });
      });
    },
    [queryClient]
  );

  const invalidateAgents = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["agents"] });
  }, [queryClient]);

  const enableActiveAgent = useCallback(async () => {
    if (!activeAgent) return;
    try {
      setIsLoadingAgentStatus(true);
      await statusService.enableAgent(activeAgent.id);
      updateAgentStatusLocally(activeAgent.id, {
        mode: "active",
        pausedUntil: null,
      });
      invalidateAgents();
      showSuccessToast("A.I. Phone Answering Enabled");
    } catch (error) {
      showErrorToast("Failed to enable agent");
      throw error;
    } finally {
      setIsLoadingAgentStatus(false);
    }
  }, [
    activeAgent,
    setIsLoadingAgentStatus,
    statusService,
    updateAgentStatusLocally,
    invalidateAgents,
  ]);

  function convertHoursToSeconds(hours: number): number {
    return hours * 60 * 60;
  }

  const pauseActiveAgent = useCallback(
    async (hours: number) => {
      if (!activeAgent) return;
      try {
        setIsLoadingAgentStatus(true);
        const response = await statusService.pauseAgent(
          activeAgent.id,
          convertHoursToSeconds(hours)
        );
        const pausedUntil = response.pausedUntil
          ? new Date(response.pausedUntil)
          : null;
        updateAgentStatusLocally(activeAgent.id, {
          mode: "paused",
          pausedUntil,
        });
        invalidateAgents();
        showSuccessToast(
          `A.I. Phone Answering Paused for ${hours} hour${hours > 1 ? "s" : ""}`
        );
      } catch (error) {
        showErrorToast("Failed to pause agent");
        throw error;
      } finally {
        setIsLoadingAgentStatus(false);
      }
    },
    [
      activeAgent,
      setIsLoadingAgentStatus,
      statusService,
      updateAgentStatusLocally,
      invalidateAgents,
    ]
  );

  const disableActiveAgent = useCallback(async () => {
    if (!activeAgent) return;
    try {
      setIsLoadingAgentStatus(true);
      await statusService.disableAgent(activeAgent.id);
      updateAgentStatusLocally(activeAgent.id, {
        mode: "disabled",
        pausedUntil: null,
      });
      invalidateAgents();
      showSuccessToast("A.I. Phone Answering Disabled");
    } catch (error) {
      showErrorToast("Failed to disable agent");
      throw error;
    } finally {
      setIsLoadingAgentStatus(false);
    }
  }, [
    activeAgent,
    setIsLoadingAgentStatus,
    statusService,
    updateAgentStatusLocally,
    invalidateAgents,
  ]);

  return (
    <ActiveAgentContext.Provider
      value={{
        activeAgent,
        setActiveAgentById,
        agents,
        agentsError,
        agentsLoading,
        hasNoAgents,
        isLoadingAgentStatus,
        enableActiveAgent,
        pauseActiveAgent,
        disableActiveAgent,
      }}
    >
      {children}
    </ActiveAgentContext.Provider>
  );
};

type ActiveAgentContextType = {
  activeAgent: IAgent | null;
  setActiveAgentById: (id: string) => void;
  agents: IAgent[] | undefined;
  agentsError: Error | null;
  agentsLoading: boolean;
  hasNoAgents: boolean;
  isLoadingAgentStatus: boolean;
  enableActiveAgent: () => Promise<void>;
  disableActiveAgent: () => Promise<void>;
  pauseActiveAgent: (hours: number) => Promise<void>;
};

export const ActiveAgentContext = createContext<
  ActiveAgentContextType | undefined
>(undefined);

export const useActiveAgent = () => {
  const context = useContext(ActiveAgentContext);
  if (context === undefined) {
    throw new Error(
      "useActiveAgent must be used within an ActiveAgentProvider"
    );
  }
  return context;
};
